'use client';

import { useState, useRef, useLayoutEffect, useEffect } from 'react';
import { gsap } from 'gsap';
import { MessageCircle, Phone, X } from 'lucide-react';

const WHATSAPP_NUMBER = '919876543210';
const CALL_NUMBER = '+91 98765 43210';

export default function FloatingContact() {
    const [isExpanded, setIsExpanded] = useState(false);
    const whatsappRef = useRef<HTMLAnchorElement>(null);
    const callRef = useRef<HTMLAnchorElement>(null);
    const toggleRef = useRef<HTMLButtonElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=Hello! I'm interested in your handcrafted furniture.`;

    useLayoutEffect(() => {
        const buttons = [whatsappRef.current, callRef.current].filter(
            Boolean
        ) as HTMLElement[];
        gsap.set(buttons, { y: 20, opacity: 0, scale: 0.5 });
    }, []);

    useEffect(() => {
        gsap.fromTo(
            containerRef.current,
            { x: -80, opacity: 0 },
            { x: 0, opacity: 1, duration: 0.8, ease: 'back.out(1.7)', delay: 0.5 }
        );
    }, []);

    useEffect(() => {
        const buttons = [whatsappRef.current, callRef.current].filter(
            Boolean
        ) as HTMLElement[];
        if (buttons.length === 0) return;

        if (isExpanded) {
            gsap.to(buttons, {
                y: 0,
                opacity: 1,
                scale: 1,
                duration: 0.4,
                stagger: 0.1,
                ease: 'back.out(1.7)',
            });
        } else {
            gsap.to(buttons, {
                y: 20,
                opacity: 0,
                scale: 0.5,
                duration: 0.25,
                stagger: 0.05,
                ease: 'power2.in',
            });
        }
    }, [isExpanded]);

    const handleToggle = () => {
        gsap.to(toggleRef.current, {
            rotation: isExpanded ? 0 : 180,
            duration: 0.3,
            ease: 'power2.inOut',
        });
        setIsExpanded((prev) => !prev);
    };

    const handleHover = (el: HTMLElement | null) => {
        if (!el) return;
        gsap.to(el, { scale: 1.15, duration: 0.2, ease: 'power2.out' });
    };

    const handleHoverOut = (el: HTMLElement | null) => {
        if (!el) return;
        gsap.to(el, { scale: 1, duration: 0.2, ease: 'power2.out' });
    };

    return (
        <div
            ref={containerRef}
            // ✅ w-14 locks the container to exactly the button width (56px)
            // nothing can overflow and block other elements
            className="fixed left-4 bottom-6 z-50 flex flex-col items-start gap-2 w-14"
        >
            {/* ── WhatsApp ── */}
            <a
                ref={whatsappRef}
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Chat on WhatsApp"
                onMouseEnter={() => handleHover(whatsappRef.current)}
                onMouseLeave={() => handleHoverOut(whatsappRef.current)}
                // ✅ relative so tooltip can be absolute inside it
                className={`group relative flex items-center ${isExpanded ? 'pointer-events-auto' : 'pointer-events-none'
                    }`}
            >
                {/* Icon */}
                <div
                    className="w-14 h-14 rounded-full shadow-lg flex items-center
                               justify-center flex-shrink-0
                               bg-gradient-to-br from-emerald-600 to-emerald-900
                               ring-2 ring-amber-300/40"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="white"
                        className="w-7 h-7"
                    >
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099
                         -.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199
                         -.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475
                         -.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458
                         .13-.606.134-.133.298-.347.446-.52.149-.174.198-.298
                         .298-.497.099-.198.05-.371-.025-.52-.075-.149-.669
                         -1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173
                         -.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272
                         .297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213
                         3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489
                         1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758
                         -.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074
                         -.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87
                         0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235
                         -.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884
                         9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825
                         0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m
                         8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16
                         5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057
                         24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005
                         c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0
                         00-3.48-8.413Z" />
                    </svg>
                </div>

                {/* ✅ Tooltip: absolute so it floats outside flow, pointer-events-none so it never blocks */}
                <span
                    className="absolute left-16 top-1/2 -translate-y-1/2
                               hidden sm:block bg-neutral-900 text-amber-100 text-sm
                               font-medium px-3 py-2 rounded-lg shadow-lg
                               pointer-events-none
                               opacity-0 group-hover:opacity-100 transition-opacity duration-200
                               whitespace-nowrap border border-amber-400/20"
                >
                    Chat on WhatsApp
                </span>
            </a>

            {/* ── Call ── */}
            <a
                ref={callRef}
                href={`tel:${CALL_NUMBER.replace(/\s/g, '')}`}
                aria-label="Call us"
                onMouseEnter={() => handleHover(callRef.current)}
                onMouseLeave={() => handleHoverOut(callRef.current)}
                className={`group relative flex items-center ${isExpanded ? 'pointer-events-auto' : 'pointer-events-none'
                    }`}
            >
                {/* Icon */}
                <div
                    className="w-14 h-14 rounded-full shadow-lg flex items-center
                               justify-center flex-shrink-0
                               bg-gradient-to-br from-neutral-700 to-neutral-900
                               ring-2 ring-amber-300/40"
                >
                    <Phone className="w-6 h-6 text-amber-200" />
                </div>

                {/* ✅ Tooltip: absolute, pointer-events-none */}
                <span
                    className="absolute left-16 top-1/2 -translate-y-1/2
                               hidden sm:block bg-neutral-900 text-amber-100 text-sm
                               font-medium px-3 py-2 rounded-lg shadow-lg
                               pointer-events-none
                               opacity-0 group-hover:opacity-100 transition-opacity duration-200
                               whitespace-nowrap border border-amber-400/20"
                >
                    Call: {CALL_NUMBER}
                </span>
            </a>

            {/* ── Toggle ── */}
            <button
                ref={toggleRef}
                onClick={handleToggle}
                aria-label="Toggle contact options"
                className="w-14 h-14 rounded-full shadow-xl flex items-center justify-center
                           bg-gradient-to-br from-zinc-900 to-black
                           ring-2 ring-amber-400/60 hover:ring-amber-400
                           transition-all"
            >
                {isExpanded
                    ? <X className="w-6 h-6 text-amber-300" />
                    : <MessageCircle className="w-6 h-6 text-amber-300" />
                }
            </button>
        </div>
    );
}